import { useTranslation } from "react-i18next";
import { Box, Typography, Pagination } from "@mui/material";

import { Service } from "../../../GoService/types/service.type";
import { ServiceCard, ServiceCardLoader } from "../../../GoService/components/ServiceCard";

import EmptyServicesFigure from "../../assets/images/empty-full-services.png";

interface ServicesListProps {
  services?: Service[];
  isDataLoading: boolean;
  meta?: {
    totalPages: number;
    currentPage: number;
  };
  onPageChange?: (page: number) => void;
}

const ServicesList = ({ services, isDataLoading, meta, onPageChange }: ServicesListProps) => {
  if (isDataLoading) {
    return <ServicesListLoader />;
  }

  if ((!isDataLoading && services?.length === 0) || !services) {
    return <EmptyList />;
  }

  return <ServicesListContent services={services} meta={meta} onPageChange={onPageChange} />;
};

export default ServicesList;

interface ServicesListContentProps {
  services: Service[];
  meta?: {
    totalPages: number;
    currentPage: number;
  };
  onPageChange?: (page: number) => void;
}

const ServicesListContent = ({ services, meta, onPageChange }: ServicesListContentProps) => {
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    onPageChange?.(page);
  };

  return (
    <Box>
      <Box sx={styles.servicesGrid}>
        {services?.map((service: Service) => (
          <ServiceCard key={service.id} service={service} width="calc(50% - 8px)" />
        ))}
      </Box>
    </Box>
  );
};

const ServicesListLoader = () => {
  return (
    <Box sx={styles.servicesGrid}>
      {[...Array(6)].map((_, index) => (
        <ServiceCardLoader key={index} width="calc(50% - 8px)" />
      ))}
    </Box>
  );
};

const EmptyList = () => {
  const { t } = useTranslation("profiles", { keyPrefix: "allServices" });
  return (
    <Box sx={styles.emptyContainer}>
      <Box component="img" sx={styles.emptyImage} src={EmptyServicesFigure} alt="Empty Reviews" />
      <Typography sx={styles.emptyTitle}>{t("noResults")}</Typography>
      <Typography sx={styles.emptySubtitle}>{t("browseCategories")}</Typography>
    </Box>
  );
};

const styles = {
  servicesGrid: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    flexWrap: "wrap",
    gap: "16px",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "24px",
    padding: "80px 16px",
  },
  emptyImage: {
    width: "300px",
    height: "225px",
  },
  emptyTitle: {
    color: "#000",
    textAlign: "center",
    fontFamily: "Montserrat",
    fontSize: "32px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  emptySubtitle: {
    fontFamily: "Montserrat",
    color: "var(--Gray-Gray-700, #5E6278)",
    textAlign: "center",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
  },
} as const;
